import { Link, navigate } from 'gatsby';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { ResponsiveContext } from '../../contexts/ResponsiveContext';
import { useCurrentPath } from '../../contexts/useCurrentPath';
import { useSiteMetadataVersions } from '../../queries/useSiteMetadataVersions';
import { DocOverview } from '../../types/docs';
import { getUrlForChangedVersion } from './sidebar.utils';
import documentationLogo from '../../images/domino-logo-large.png';
import { MainPath } from '../../types/types';
import { buildAppUrl } from '../../utils/buildAppUrl';
import { useLocation } from '../../contexts/useLocation';
import { useMemo } from 'react';
import { compareVersions } from '../../utils/getIntegralVersion';
import { useVersionLookup } from '../../utils/getVersionLookup';
import { FaBars, FaSearch } from 'react-icons/fa';
import SidebarMenu from './components/SidebarMenu';
import ToggleNightMode from '../nightToggle/ToggleNightMode';
import { MenuItemType, MenuProps, SubMenuItemProps, MenuItemProps } from './types';
import VersionPicker from './components/VersionPicker';
import GuideScroller from './components/GuideScroller';
import MobileSearchDrawer from '../shared/MobileSearchDrawer';

const getLinkWithPath = (doc: DocOverview, url: string) => (
  <Link
    key={doc.id}
    to={url}
    id={doc.id}
    className="text-bright-white text-sm font-medium pl-0 hover:text-vivid-orange w-full flex items-center min-h-6 p-1 h-auto"
  >
    {doc.sidebar || doc.title}
  </Link>
);

const getSubMenuItems = (props: SubMenuItemProps): MenuItemType => {
  const { doc, latestVersion, version, currentPath, location } = props;
  const url = buildAppUrl({
    latestVersion,
    permalink: doc.permalink,
    slug: doc.slug,
    title: doc.title,
    version,
    path: currentPath,
  });
  if (doc.children.length === 0) {
    return {
      key: doc.id,
      version: doc.version,
      permalink: doc.permalink,
      label: getLinkWithPath(doc, url),
    };
  } else {
    return {
      key: doc.id,
      version: doc.version,
      permalink: doc.permalink,
      label: getLinkWithPath(doc, url),
      ...(doc.children.length > 0 && {
        children: getMenuItemChildren({
          doc,
          currentPath,
          version,
          latestVersion,
          location,
        }),
      }),
    };
  }
};

const getMenuItemChildren = (props: SubMenuItemProps): MenuItemType[] =>
  props.doc.children.map((doc: DocOverview) => getSubMenuItems({ ...props, doc }));

const getMenuItems = (props: MenuItemProps): MenuItemType[] => {
  const { docs, latestVersion, version, currentPath, location } = props;

  return docs.flatMap((doc, index) => {
    const items: any[] = []; // Create an array to hold items for this iteration

    if (doc['section']) {
      items.push({
        key: `section-${index}`,
        label: (
          <span
            className={`bg-very-dark-grey cursor-default text-section-text h-10 pr-1 pt-2 pb-2 font-semibold text-sm w-11/12 text-left block uppercase ${doc.separator === 'true' ? 'border-t border-dark-charcoal' : ''}`}
          >
            {doc.section}
          </span>
        ),
        className: 'section-header',
        disabled: true,
      }); // Add the section header to the items array
    }

    const item = getSubMenuItems({
      doc,
      currentPath,
      version,
      latestVersion,
      location,
    });

    // If there is no section but a separator, add a bottom border to the menu item
    if (!doc.section && doc.separator === 'true') {
      item.className = 'border-b border-dark-charcoal';
    }

    items.push(item);
    return items;
  });
};

const Sidebar = (props: MenuProps) => {
  const { categoryVersions, version, docs, permalink, title } = props;
  const { dispatch } = useContext(ResponsiveContext);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const location = useLocation();
  const versionLookup = useVersionLookup(); // Move this up
  const currentPath = useCurrentPath();
  const { latestVersion } = useSiteMetadataVersions();
  const [searchOpen, setSearchOpen] = useState(false);
  const sidebarScrollRef = useRef<HTMLDivElement>(null);

  function versionCompare(a: any, b: any) {
    if (!a?.label?.props || !b?.label?.props) return 0;
    const propsA = a.label.props;
    const propsB = b.label.props;

    // Safe navigation for path comparison
    const pathA = propsA?.to || '';
    const pathB = propsB?.to || '';

    if (pathA === '/release_notes/preview-features/') return -1;
    if (pathB === '/release_notes/preview-features/') return +1;

    // Only compare versions if both exist
    if (a?.version && b?.version) {
      return compareVersions(b.version, a.version);
    }
    return 0;
  }

  const items = useMemo(() => {
    return getMenuItems({
      docs,
      currentPath: currentPath as MainPath,
      version,
      latestVersion,
      location,
    });
  }, [docs, currentPath, version, latestVersion, location]);

  const getSortedItems = (items: MenuItemType[], currentPath: string | null): MenuItemType[] => {
    if (currentPath !== 'release_notes') return items;
    return [...items].sort(versionCompare);
  };

  const sortedItems: MenuItemType[] = useMemo(() => {
    return getSortedItems(items, currentPath);
  }, [items, currentPath]);

  const changeSelectedVersion = (version: string) => {
    if (currentPath == null) return;
    if (currentPath === 'release_notes') return;
    const url = getUrlForChangedVersion({
      latestVersion: latestVersion,
      version: version,
      title: title,
      currentPath: currentPath,
      permalink: permalink,
      versionLookup: versionLookup,
    });
    navigate(url);
  };

  /** Recursively finds the selected key and its parent keys */
  const findOpenKeys = (doc: DocOverview, parentKeys: string[] = []): string[] | null => {
    const url = buildAppUrl({
      latestVersion,
      permalink: doc.permalink,
      slug: doc.slug,
      title: doc.title,
      version,
      path: currentPath as MainPath,
    });

    // Early exact match check
    if (location.pathname === url) {
      return [...parentKeys, doc.id];
    }

    // Check children only if needed
    if (doc.children && doc.children.length > 0) {
      for (const child of doc.children) {
        const result = findOpenKeys(child, [...parentKeys, doc.id]);
        if (result) return result;
      }
    }

    return null;
  };

  useEffect(() => {
    let foundKeys = null;

    for (const doc of docs) {
      const result = findOpenKeys(doc);
      if (result) {
        foundKeys = result;
        break;
      }
    }

    setSelectedKeys(foundKeys || []);
    setOpenKeys(foundKeys || []);
  }, [version, title, docs, currentPath]);

  useEffect(() => {
    if (!selectedKeys.length) return;

    setTimeout(() => {
      const activeId = selectedKeys[selectedKeys.length - 1];
      const element = document.getElementById(activeId);
      const scrollContainer = document.querySelector('#leftnav .overflow-y-scroll');

      if (element && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        const containerHeight = scrollContainer.clientHeight;
        const newScrollTop = relativeTop - containerHeight / 2;

        scrollContainer.scrollTop = newScrollTop;
      }
    }, 150);
  }, [selectedKeys]);

  const handleMenuAction = (key: string) => {
    setOpenKeys(
      (prev) =>
        prev.includes(key)
          ? prev.filter((k) => k !== key) // Close if open
          : [...prev, key] // Open if closed
    );
  };

  return (
    <>
      {/* Hamburger Menu (Visible on Mobile) */}

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex md:hidden fixed mt-0.5 top-3 right-4 w-8 h-8 text-very-dark-grey items-center justify-center dark:text-bright-white text-2xl bg-bright-white dark:bg-very-dark-grey border border-medium-dark-grey cursor-pointer z-40 rounded"
      >
        <FaBars className="w-6 h-4" />
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed md:sticky 
          top-0 left-0 bottom-0 
          bg-very-dark-grey 
          h-screen
          transition-all duration-300 
          z-40
          ${collapsed ? '-translate-x-full md:translate-x-0' : 'translate-x-0'}
          ${collapsed ? 'w-0 md:w-80' : 'w-full md:w-80'}
          ${collapsed ? 'overflow-hidden md:overflow-visible' : 'overflow-visible'}
          ${!collapsed ? 'sidebar-open' : ''}
          
        `}
      >
        {/* Close button for mobile */}

        <div id="leftnav" ref={sidebarScrollRef} className="h-full flex flex-col overflow-y-auto sidebar-leftnav z-40">
          <div className="flex flex-row items-center gap-2 h-10 ml-2 sm:ml-8 pr-4 mt-4 mb-4 pb-2">
            <div className="grow justify-start">
              <Link className="w-28" to="/" onClick={() => setCollapsed(true)}>
                <img
                  loading="lazy"
                  src={documentationLogo}
                  className="cursor-pointer w-32 h-auto m-0"
                  alt="domino logo"
                  id="domino-logo"
                />
              </Link>
            </div>
            <div className="w-12 flex grow md:hidden justify-end">
              <ToggleNightMode
                visible={collapsed ? false : true}
                darkBackground="bg-medium-dark-grey"
                lightBackground="bg-medium-dark-grey"
                sunStroke="stroke-bright-white"
                moonStroke="stroke-bright-white"
                lightDotColour="after:bg-bright-white"
                darkDotColour=""
              />
            </div>
            <div className="w-8 flex justify-end">
              <button
                onClick={() => setSearchOpen((prev) => !prev)}
                className={`w-8 h-8 md:hidden border border-medium-dark-grey cursor-pointer z-50 rounded flex items-center justify-center ${
                  searchOpen ? 'text-vivid-orange' : 'text-white'
                }`}
              >
                <FaSearch />
              </button>
            </div>
            <div className="w-8 flex">
              <button
                onClick={() => setCollapsed(true)}
                className="md:hidden w-8 h-8 text-white text-2xl bg-transparent border border-medium-dark-grey cursor-pointer z-50 rounded"
              >
                &times;
              </button>
            </div>
          </div>
          <div
            className={`w-full md:hidden items-center border-medium-dark-grey ${searchOpen ? 'border-b border-t' : 'border-b-0 border-t'}`}
          >
            <MobileSearchDrawer searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
          </div>

          <div className="flex justify-center ml-8 mr-4 mb-2">
            <a
              href="https://docs.domino.ai"
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-vivid-orange text-very-dark-grey px-4 py-1 text-sm font-semibold hover:bg-bright-white transition-colors"
            >
              Latest docs
            </a>
          </div>

          {currentPath && currentPath !== 'release_notes' && (
            <VersionPicker
              currentPath={currentPath}
              version={version}
              categoryVersions={categoryVersions}
              latestVersion={latestVersion}
              changeSelectedVersion={changeSelectedVersion}
              dispatch={dispatch}
            />
          )}

          <SidebarMenu
            sortedItems={sortedItems}
            openKeys={openKeys}
            selectedKeys={selectedKeys}
            handleMenuAction={handleMenuAction}
          />
          <GuideScroller />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
